import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Settings } from 'api/landingPageTheme'
import { initialAppLandingPageThemeState } from './initialState'
import { AppLandingPageTheme, ContentBlockData } from './types'

// Helper function to process form featured_contents data
function processFormFeaturedContents(
  existingFeaturedContents: any,
  formFeaturedContents: any
) {
  const newFeaturedContents: any = {}

  // Copy over all slides from formFeaturedContents, merging with existing data
  Object.keys(formFeaturedContents).forEach(slideId => {
    const formSlide = formFeaturedContents[slideId]
    if (formSlide && typeof formSlide === 'object') {
      newFeaturedContents[slideId] = {
        ...(existingFeaturedContents?.[slideId] ?? {}),
        ...formSlide,
      }
    }
    // If formSlide is null/undefined, skip -> slide is effectively deleted.
  })

  return newFeaturedContents
}

const slice = createSlice({
  name: 'customHomePage',
  initialState: initialAppLandingPageThemeState as AppLandingPageTheme,
  reducers: {
    updateContentBlock: (
      state,
      action: PayloadAction<{ key: string; data: ContentBlockData }>
    ) => {
      const { key, data } = action.payload
      const existingBlock = state.contentBlocks[key] || {}

      // Handle featured_contents separately to avoid mutation issues
      if ('featured_contents' in data && data.featured_contents) {
        const existingFeaturedContents =
          'featured_contents' in existingBlock
            ? existingBlock.featured_contents || {}
            : {}
        const processedFeaturedContents = processFormFeaturedContents(
          existingFeaturedContents,
          data.featured_contents
        )

        state.contentBlocks[key] = {
          ...existingBlock,
          ...data,
          featured_contents: processedFeaturedContents,
        }
      } else {
        state.contentBlocks[key] = {
          ...existingBlock,
          ...data,
        }
      }
    },
    deleteContentBlock: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload
      delete state.contentBlocks[key]
    },
    updateCarouselSlide: (
      state,
      action: PayloadAction<{
        carouselId: string
        slideId: string
        slideData: any
      }>
    ) => {
      const { carouselId, slideId, slideData } = action.payload
      const carousel = state.contentBlocks[carouselId]

      if (carousel && 'featured_contents' in carousel) {
        // Ensure featured_contents exists and is an object
        if (!carousel.featured_contents) {
          carousel.featured_contents = {}
        }

        // Update the specific slide
        carousel.featured_contents = {
          ...carousel.featured_contents,
          [slideId]: {
            ...carousel.featured_contents[slideId],
            ...slideData,
          },
        }
      }
    },
    updateCarouselFeaturedContents: (
      state,
      action: PayloadAction<{ carouselId: string; featuredContents: any }>
    ) => {
      const { carouselId, featuredContents } = action.payload
      const carousel = state.contentBlocks[carouselId]

      if (carousel && 'featured_contents' in carousel) {
        // Replace the entire featured_contents object
        carousel.featured_contents = featuredContents
      }
    },
    updateSettings: (state, action: PayloadAction<Partial<Settings>>) => {
      state.settings = {
        ...state.settings,
        ...action.payload,
      }
    },
    reset: (state, action: PayloadAction<AppLandingPageTheme>) => {
      const { contentBlocks, settings } = action.payload
      state.contentBlocks = contentBlocks
      state.settings = settings
    },
  },
})

export const reducer = slice.reducer
export const {
  updateContentBlock,
  deleteContentBlock,
  updateCarouselSlide,
  updateCarouselFeaturedContents,
  updateSettings,
  reset,
} = slice.actions
export const selectAppLandingPageTheme = (state: {
  appLandingPageTheme: AppLandingPageTheme
}) => state.appLandingPageTheme

// Simple selector that returns all content blocks
export const selectAllContentBlocks = (state: {
  appLandingPageTheme: AppLandingPageTheme
}) => state.appLandingPageTheme.contentBlocks

// Basic filtering with current time rounded to the hour for stability
export const selectActiveContentBlocks = (state: {
  appLandingPageTheme: AppLandingPageTheme
}) => {
  const allBlocks = state.appLandingPageTheme.contentBlocks
  // Round to the hour to reduce re-render frequency
  const now = new Date()
  now.setMinutes(0, 0, 0) // Round to hour
  const testCurrentTime = now.toISOString()
  const activeBlocks: AppLandingPageTheme['contentBlocks'] = {}

  Object.keys(allBlocks).forEach(blockId => {
    const block = allBlocks[blockId]
    // Safety check: if block is null/undefined, skip
    if (!block) {
      return
    }
    // Include block if it has no end_at or hasn't expired yet
    if (!block.end_at || block.end_at >= testCurrentTime) {
      activeBlocks[blockId] = block
    }
  })

  return activeBlocks
}

// Basic filtering with current time rounded to the hour for stability
export const selectExpiredContentBlocks = (state: {
  appLandingPageTheme: AppLandingPageTheme
}) => {
  const allBlocks = state.appLandingPageTheme.contentBlocks
  // Round to the hour to reduce re-render frequency
  const now = new Date()
  now.setMinutes(0, 0, 0) // Round to hour
  const testCurrentTime = now.toISOString()
  const expiredBlocks: AppLandingPageTheme['contentBlocks'] = {}

  Object.keys(allBlocks).forEach(blockId => {
    const block = allBlocks[blockId]
    // Safety check: if block is null/undefined, skip
    if (!block) {
      return
    }
    // Include block if it has end_at and has expired
    if (block.end_at && block.end_at < testCurrentTime) {
      expiredBlocks[blockId] = block
    }
  })

  return expiredBlocks
}
